/**
 * Web entry point — bare-name re-exports of the DOM implementations.
 *
 * Deployed web surfaces import from `@galapagos/design-system/web` so the
 * bundle never evaluates the `.native` modules the main barrel resolves to
 * (which would pull `react-native` into a browser build). Same-package sister
 * entry per ADR-024, following the `./registry.web` precedent.
 */

export { Autocomplete } from './components/Autocomplete/Autocomplete.web';
export type { WebAutocompleteProps } from './components/Autocomplete/Autocomplete.web';
export { AutocompleteEmpty } from './components/AutocompleteEmpty/AutocompleteEmpty.web';
export type { WebAutocompleteEmptyProps } from './components/AutocompleteEmpty/AutocompleteEmpty.web';
export { BackButton } from './components/BackButton/BackButton.web';
export type { WebBackButtonProps } from './components/BackButton/BackButton.web';
export { Button } from './components/Button/Button.web';
export type { WebButtonProps } from './components/Button/Button.web';
export { WebButtonWidth } from './components/Button/Button';
export { Callout } from './components/Callout/Callout.web';
export type { WebCalloutProps, WebCalloutTone } from './components/Callout/Callout.web';
export { Checkbox } from './components/Checkbox/Checkbox.web';
export type { WebCheckboxProps } from './components/Checkbox/Checkbox.web';
export { CheckboxField } from './components/CheckboxField/CheckboxField.web';
export type { WebCheckboxFieldProps } from './components/CheckboxField/CheckboxField.web';
export { Chip } from './components/Chip/Chip.web';
export { DateChip } from './components/DateChip/DateChip.web';
export type { WebDateChipProps } from './components/DateChip/DateChip.web';
export { DateInput } from './components/DateInput/DateInput.web';
export type { DateInputCopy, WebDateInputProps } from './components/DateInput/DateInput.web';
export { Dialog } from './components/Dialog/Dialog.web';
export type { WebDialogProps } from './components/Dialog/Dialog.web';
export { Dismissible } from './components/Dismissible/Dismissible.web';
export type { WebDismissibleProps } from './components/Dismissible/Dismissible.web';
export { registerDismissalStore, useDismissed } from './components/Dismissible/dismissal-store';
export type { DismissalStore } from './components/Dismissible/dismissal-store';
export { Divider } from './components/Divider/Divider.web';
export { ExpandableCallout } from './components/ExpandableCallout/ExpandableCallout.web';
export type { WebExpandableCalloutProps } from './components/ExpandableCallout/ExpandableCallout.web';
export { FieldError } from './components/FieldError/FieldError.web';
export type { WebFieldErrorProps } from './components/FieldError/FieldError.web';
export { Icon } from './components/Icon/Icon.web';
export type { WebIconProps } from './components/Icon/Icon.web';
// Lets a host check a served icon name before rendering it, without a cast.
export { iconNameSet } from './elements/icons';
export type { IconName } from './elements/icons';
export { InfoDot } from './components/InfoDot/InfoDot.web';
export type { WebInfoDotProps } from './components/InfoDot/InfoDot.web';
export { Label } from './components/Label/Label.web';
export { Link } from './components/Link/Link.web';
export type { WebLinkProps } from './components/Link/Link.web';
export { MultiSelect } from './components/MultiSelect/MultiSelect.web';
export { NumberPicker } from './components/NumberPicker/NumberPicker.web';
export type { WebNumberPickerProps } from './components/NumberPicker/NumberPicker.web';
export { OtpInput } from './components/OtpInput/OtpInput.web';
export type { WebOtpInputProps } from './components/OtpInput/OtpInput.web';
export { PageHeader } from './components/PageHeader/PageHeader.web';
export type { WebPageHeaderProps } from './components/PageHeader/PageHeader.web';
export { Pagination } from './components/Pagination/Pagination.web';
export type { WebPaginationProps } from './components/Pagination/Pagination.web';
export { PhoneInput } from './components/PhoneInput/PhoneInput.web';
export type { WebPhoneInputProps } from './components/PhoneInput/PhoneInput.web';
export { PressableSurface } from './components/PressableSurface/PressableSurface.web';
export { ProgressBar } from './components/ProgressBar/ProgressBar.web';
export type { WebProgressBarProps } from './components/ProgressBar/ProgressBar.web';
export { Radio } from './components/Radio/Radio.web';
export type { WebRadioProps } from './components/Radio/Radio.web';
export { Screen, ScreenLayoutProvider } from './components/Screen/Screen.web';
export type {
  ScreenLayout,
  ScreenLayoutProviderProps,
  WebScreenProps,
} from './components/Screen/Screen.web';
export { ScrollableWeekStrip } from './components/ScrollableWeekStrip/ScrollableWeekStrip.web';
export type { WebScrollableWeekStripProps } from './components/ScrollableWeekStrip/ScrollableWeekStrip.web';
export { Select } from './components/Select/Select.web';
export type { WebSelectProps } from './components/Select/Select.web';
export { SingleSelect } from './components/SingleSelect/SingleSelect.web';
export type { WebSingleSelectProps } from './components/SingleSelect/SingleSelect.web';
export { singleSelectOptionSchema } from './components/SingleSelect/SingleSelect';
export type { OptionTag, SingleSelectOption } from './components/SingleSelect/SingleSelect';
export { Spinner } from './components/Spinner/Spinner.web';
export type { WebSpinnerProps } from './components/Spinner/Spinner.web';
export { Stack } from './components/Stack/Stack.web';
export type { WebStackProps } from './components/Stack/Stack.web';
export { StarRating } from './components/StarRating/StarRating.web';
export type { WebStarRatingProps } from './components/StarRating/StarRating.web';
export { Tabs } from './components/Tabs/Tabs.web';
export type { WebTabsProps } from './components/Tabs/Tabs.web';
export { Text } from './components/Text/Text.web';
export type { WebTextProps } from './components/Text/Text.web';
export { TextButton } from './components/TextButton/TextButton.web';
export type { WebTextButtonProps } from './components/TextButton/TextButton.web';
export { TextInput } from './components/TextInput/TextInput.web';
export type { WebTextInputProps } from './components/TextInput/TextInput.web';
export { Toast } from './components/Toast/Toast.web';
export { ensureFocusVisibleStyle } from './elements/focusStyle.web';
export { shadows } from './elements/shadows';
export type { WebToastProps } from './components/Toast/Toast.web';
// GenUI hosts own tool analytics, so a web flow host wraps rendered flow
// content in the suppression provider (same treatment as the native host).
export { AnalyticsActionSuppressionProvider } from './analytics/AnalyticsActionContext';

export { skeletonBlockPropsSchema } from './components/SkeletonBlock/SkeletonBlock';
export type { SkeletonBlockProps } from './components/SkeletonBlock/SkeletonBlock';
export { SkeletonBlock } from './components/SkeletonBlock/SkeletonBlock.web';

export { skeletonTextPropsSchema } from './components/SkeletonText/SkeletonText';
export type { SkeletonTextProps } from './components/SkeletonText/SkeletonText';
export { SkeletonText } from './components/SkeletonText/SkeletonText.web';

export { progressListCardSkeletonPropsSchema } from './components/ProgressListCard/ProgressListCardSkeleton';
export type { ProgressListCardSkeletonProps } from './components/ProgressListCard/ProgressListCardSkeleton';
export { ProgressListCardSkeleton } from './components/ProgressListCard/ProgressListCardSkeleton.web';
